import { Component, computed, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { UiButtonComponent } from '@app/shared/ui/button/ui-button.component';

import { TaskCopySettingsDTO } from '@app/shared/services/dtos/project-tasks/copy-task-to-project.dto';
import { TasksDataService } from '@app/shared/services/tasks-data.service';
import { Project } from '@app/shared/types/models/project/project.model';
import { Task } from '@app/shared/types/models/task/task.model';
import { UiDialogComponent } from '@app/shared/ui/dialog/ui-dialog.component';
import { UiSelectComponent } from '@app/shared/ui/select/ui-select.component';
import { UiSpinnerDirective } from '@app/shared/ui/spinner/ui-spinner.directive';
import { TranslatePipe } from '@ngx-translate/core';

export interface CopyTaskDialogData {
  task: Task;
  projects: Project[];
  activeProject: Project;
}

export interface CopyTaskDialogResult {
  task: Task;
  targetProject: Project;
}

interface CopySettingOption {
  key: keyof TaskCopySettingsDTO;
  labelKey: string;
}

@Component({
  selector: 'proffeo-copy-task-dialog',
  templateUrl: './copy-task-dialog.component.html',
  imports: [
    UiDialogComponent,
    MatCheckboxModule,
    MatDialogModule,
    UiSelectComponent,
    TranslatePipe,
    UiButtonComponent,
    UiSpinnerDirective
  ]
})
export class CopyTaskDialogComponent {
  private readonly dialogRef: MatDialogRef<CopyTaskDialogComponent, CopyTaskDialogResult> = inject(
    MatDialogRef<CopyTaskDialogComponent, CopyTaskDialogResult>
  );
  private readonly destroyRef = inject(DestroyRef);
  private readonly tasksDataService: TasksDataService = inject(TasksDataService);

  protected readonly data: CopyTaskDialogData = inject(MAT_DIALOG_DATA);

  protected readonly isCopying = signal(false);
  protected readonly selectedProject = signal<Project>(this.data.activeProject);

  protected readonly settings = signal<TaskCopySettingsDTO>({
    includeDescription: true,
    includeEstimation: true,
    includeStartDate: true,
    includeEndDate: true,
    includeChildren: true,
    includeProgress: true,
    includeStatus: true,
    includePriority: true,
    includeLocked: true,
    includeFiles: true
  });

  protected readonly options: readonly CopySettingOption[] = [
    { key: 'includeDescription', labelKey: 'copy-task-modal.include-description' },
    { key: 'includeEstimation', labelKey: 'copy-task-modal.include-estimation' },
    { key: 'includeStartDate', labelKey: 'copy-task-modal.include-start-date' },
    { key: 'includeEndDate', labelKey: 'copy-task-modal.include-end-date' },
    { key: 'includeChildren', labelKey: 'copy-task-modal.include-children' },
    { key: 'includeProgress', labelKey: 'copy-task-modal.include-progress' },
    { key: 'includeStatus', labelKey: 'copy-task-modal.include-status' },
    { key: 'includePriority', labelKey: 'copy-task-modal.include-priority' },
    { key: 'includeLocked', labelKey: 'copy-task-modal.include-locked' },
    { key: 'includeFiles', labelKey: 'copy-task-modal.include-files' }
  ];

  protected readonly allSelected = computed(() => this.options.every(option => this.settings()[option.key]));

  protected readonly someSelected = computed(
    () => !this.allSelected() && this.options.some(option => this.settings()[option.key])
  );

  protected readonly projectOptions = computed(() =>
    this.data.projects.map(project => ({
      value: project,
      label: project.name
    }))
  );

  protected readonly compareProjects = (first: Project, second: Project): boolean => first?.id === second?.id;

  protected onProjectSelected(project: Project | null): void {
    if (!project) {
      return;
    }
    this.selectedProject.set(project);
  }

  protected toggle(key: keyof TaskCopySettingsDTO, checked: boolean): void {
    this.settings.update(current => ({ ...current, [key]: checked }));
  }

  protected toggleAllCheckboxes(checked: boolean): void {
    this.settings.update(current => {
      const next = { ...current };
      for (const option of this.options) {
        next[option.key] = checked;
      }
      return next;
    });
  }

  protected onCopy(): void {
    if (this.isCopying()) return;

    this.isCopying.set(true);
    this.dialogRef.disableClose = true;

    this.tasksDataService
      .copyTaskToProject(this.data.task.id, {
        newProjectId: this.selectedProject().id,
        settings: this.settings()
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: task => this.dialogRef.close({ task, targetProject: this.selectedProject() }),
        error: () => {
          this.isCopying.set(false);
          this.dialogRef.disableClose = false;
        }
      });
  }
}
